import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFreelancerProposal, getFreelancerProposalByProject } from "../api/freelancer-proposals-api";
import {
  FreelancerProposal,
  CreateFreelancerProposalRequest
} from "../models/FreelancerProposal";

/**
 * Hook to get the current freelancer's proposal for a specific project
 * @param projectId The project ID
 * @param enablePolling Whether to enable automatic refetching (default: false).
 * @returns The freelancer's proposal or null if none exists
 */
export const useFreelancerProposalByProject = (projectId: number, enablePolling: boolean = false) => {
  return useQuery({
    queryKey: ["freelancer-proposal", "project", projectId],
    queryFn: () => getFreelancerProposalByProject(projectId),
    enabled: !!projectId,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: enablePolling ? 60000 : false, // Polling every 60s when enabled
    refetchOnWindowFocus: false, // Prevent refetch on window focus
  });
};

/**
 * Hook to create or update a freelancer proposal (draft or final submission based on status)
 * Uses PUT with upsert behavior: creates new proposal or updates existing one
 */
export const useCreateFreelancerProposal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (proposalData: CreateFreelancerProposalRequest) =>
      createFreelancerProposal(proposalData),
    onSuccess: (data: FreelancerProposal) => {
      // Invalidate project-related queries to refresh the data
      queryClient.invalidateQueries({
        queryKey: ["freelancer-proposal", "project", data.projectId]
      });
    },
  });
};